import React from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { styled } from '@mui/system';

// Shared empty state for slide-mode "no data" surfaces (vacancies, advice,
// Q&A, education). Three-line vertical stack: illustration glyph → title →
// optional description → optional primary action. Used as the slide body
// when the underlying data list is empty.

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '32px 24px 16px',
  margin: '0 auto',
  maxWidth: 520,
  flex: 1,
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: '24px 16px 12px'
  }
}));

const IllustrationCircle = styled('div')(({ theme, tone }) => {
  const tones = {
    primary: {
      ring: 'rgba(121, 201, 18, 0.18)',
      core: 'rgba(185, 248, 51, 0.45)',
      icon: theme.palette.primary.main
    },
    neutral: {
      ring: 'rgba(0, 0, 0, 0.06)',
      core: 'rgba(0, 0, 0, 0.04)',
      icon: theme.palette.grey[300]
    },
    accent: {
      ring: 'rgba(48, 176, 199, 0.18)',
      core: 'rgba(48, 176, 199, 0.10)',
      icon: theme.palette.tertiary.main
    }
  };
  const t = tones[tone] || tones.primary;
  return {
    width: 112,
    height: 112,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${t.core} 0%, ${t.ring} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    '& svg': {
      width: 48,
      height: 48,
      color: t.icon,
      stroke: t.icon
    }
  };
});

const Title = styled(Typography)(() => ({
  fontSize: 22,
  fontWeight: 600,
  lineHeight: 1.3,
  marginBottom: 8
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: 15,
  lineHeight: 1.55,
  color: theme.palette.grey[400],
  marginBottom: 24,
  maxWidth: 420
}));

const Actions = styled('div')(() => ({
  display: 'flex',
  gap: 12,
  flexWrap: 'wrap',
  justifyContent: 'center'
}));

const PrimaryAction = styled(Button)(() => ({
  padding: '10px 28px',
  fontSize: 15,
  '& svg': {
    width: 20,
    height: 20
  }
}));

const SecondaryAction = styled(Button)(({ theme }) => ({
  padding: '10px 24px',
  fontSize: 15,
  color: theme.palette.grey[400]
}));

const SlideEmptyState = ({
  icon,
  title,
  description,
  actionLabel,
  actionIcon,
  onAction,
  secondaryLabel,
  onSecondary,
  tone = 'primary'
}) => {
  return (
    <Wrapper>
      {icon ? <IllustrationCircle tone={tone}>{icon}</IllustrationCircle> : null}
      {title ? <Title variant="h3">{title}</Title> : null}
      {description ? <Description variant="body1">{description}</Description> : null}
      {(actionLabel || secondaryLabel) && (
        <Actions>
          {actionLabel ? (
            <PrimaryAction
              variant="contained"
              color="primary"
              onClick={onAction}
              endIcon={actionIcon || null}>
              {actionLabel}
            </PrimaryAction>
          ) : null}
          {secondaryLabel ? (
            <SecondaryAction variant="text" onClick={onSecondary}>
              {secondaryLabel}
            </SecondaryAction>
          ) : null}
        </Actions>
      )}
    </Wrapper>
  );
};

export default SlideEmptyState;
